import type { DeliveryListTotals, ScheduledDelivery } from "../api/types";

/**
 * What a scheduled delivery is worth, read off the SERVER's own figures.
 *
 * G-121 S2 made `src/modules/deliveries/delivery-money.ts` the one place that
 * values a delivery line: every delivery now arrives with a `totals` block
 * and every line with `money`. This module is the adapter from those decimal
 * STRINGS to the numbers the list, the phone cards and the detail strip add
 * up and compare. It does no arithmetic of its own — the two client-side
 * copies it replaced valued a shortage at the typed unit cost even after the
 * supplier's paper said otherwise, which is exactly how two rules drift.
 *
 * Billed vs expected: `billedEur` is the supplier's own total and is present
 * only when every line came from an invoice (never a partial sum, the
 * server's rule). A delivery typed by hand has it null, and the screens fall
 * back to the expected value, labelled as typed. A shortage is information —
 * billed but not received — never a receivable.
 */
/**
 * Which money figure leads a delivery and what it is called (OD-126): the
 * supplier's billed total when known, otherwise the expected value — "typed"
 * only when no invoice stands behind it at all. One rule for the list, the
 * phone cards and the detail strip.
 */
export type DeliveryHeadlineKind = "billed" | "expected" | "typed";

export function deliveryHeadline(
  totals: Pick<DeliveryTotals, "billedEur" | "expectedEur">,
  hasInvoice: boolean,
): { amount: number; kind: DeliveryHeadlineKind; label: string } {
  if (totals.billedEur !== null) {
    return { amount: totals.billedEur, kind: "billed", label: "Billed" };
  }
  return hasInvoice
    ? { amount: totals.expectedEur, kind: "expected", label: "Expected" }
    : { amount: totals.expectedEur, kind: "typed", label: "Expected (typed)" };
}

export interface DeliveryTotals {
  /** Every line's quantity at its typed unit cost. */
  expectedEur: number;
  /** The paper's total; null unless every line carries one. */
  billedEur: number | null;
  /** What turned up, valued by the server. 0 until a line is confirmed. */
  arrivedEur: number;
  /** The value of what was billed (or expected) and did not arrive. */
  shortageEur: number;
  /** The value of what arrived beyond the billed (or expected) quantity. */
  overageEur: number;
  /** How many lines a florist has confirmed, out of how many were ordered. */
  confirmedLines: number;
  totalLines: number;
}

/** Decimal-string money off the wire; anything unparseable is worth nothing. */
function num(value: string | null): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The billed figure keeps its "unknown": null stays null, and a string that
 * will not parse is treated as unknown too rather than as €0.00 billed.
 */
function nullableNum(value: string | null): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function deliveryTotals(delivery: ScheduledDelivery): DeliveryTotals {
  const t = delivery.totals;
  return {
    expectedEur: num(t.expectedEur),
    billedEur: nullableNum(t.billedEur),
    arrivedEur: num(t.receivedEur),
    shortageEur: num(t.shortageEur),
    overageEur: num(t.overageEur),
    confirmedLines: t.confirmedLines,
    totalLines: t.totalLines,
  };
}

/** How many lines came in under what was billed or ordered — the server's count. */
export function shortLineCount(delivery: ScheduledDelivery): number {
  return delivery.totals.shortLines;
}

/**
 * G-169: the Incoming stock strip's figures are the server's
 * (`GET /scheduled-deliveries/totals`, `sumDeliveryMoneyTotals`), summed there
 * over the listed deliveries with the same billed rule one level up; this
 * only reads them as numbers.
 */
export function deliveryListTotals(totals: DeliveryListTotals): DeliveryTotals {
  return {
    expectedEur: num(totals.expectedEur),
    billedEur: nullableNum(totals.billedEur),
    arrivedEur: num(totals.receivedEur),
    shortageEur: num(totals.shortageEur),
    overageEur: num(totals.overageEur),
    confirmedLines: totals.confirmedLines,
    totalLines: totals.totalLines,
  };
}
